import CommercialLayout from "../../../components/CommercialLayout";
import { useStores } from "../../../context/storesContext";
import { TurnoverObjective } from "../../../types/commercial";
import CommercialContentPage from "../../../components/CommercialContentPage";
import { fetcherWithStoreKey } from "../../../utils/fetcher";
import useSWR from "swr";
import { Range } from "../../../utils/range";
import { EcomServiceType } from "../../../utils/ecomServiceType";
import { useKpiVisibility } from "../../../kpis/useKpiVisibility";
import useBreakPoint from "../../../utils/useBreakPoint";
import CommercialContentPageDesktop from "../../../components/CommercialContentPageDesktop";
import { useAuth } from "../../../Auth/AuthProvider";
import { isEmailInWhitelist } from "../../../utils/whiteListUtils";
import useLandscape from "../../../utils/useLandscape";

function LastMonth() {
  const { user } = useAuth();
  const { selectedStore } = useStores();
  const { isKpiExists } = useKpiVisibility();

  const currentView = useBreakPoint();
  const isLandscape = useLandscape();
  const isDesktopOrTablet =
    currentView === "desktop" ||
    currentView === "tablet" ||
    (currentView === "phone" && isLandscape);
  const displayLegacyUi = !isEmailInWhitelist("ECOMMERCE_NEW_IHM_WHITE_LIST", user) || !isDesktopOrTablet;

  const { data: turnoverData } = useSWR(
    () =>
      selectedStore?.storeKey && isKpiExists("Commercial", "Turnover")
        ? [`/commercial/turnover?range=${Range.LastMonth}`,
            selectedStore.storeKey, selectedStore.countryKey as string]
        : null,
    ([url, storeEan, countryKey]) => fetcherWithStoreKey(url, storeEan, countryKey)
  );

  const { data: pastTurnoverData } = useSWR(
    () =>
      selectedStore?.storeKey && isKpiExists("Commercial", "Turnover")
        ? [`/commercial/turnover?range=${Range.LastMonthLastYear}`,
            selectedStore.storeKey, selectedStore.countryKey as string]
        : null,
    ([url, storeEan, countryKey]) => fetcherWithStoreKey(url, storeEan, countryKey)
  );

  const { data: turnoverPromoData } = useSWR(
    () =>
      selectedStore?.storeKey && isKpiExists("Commercial", "TurnoverPromo")
        ? [`/commercial/turnoverPromo?range=${Range.LastMonth}`, selectedStore.storeKey]
        : null,
    ([url, storeKey]) => fetcherWithStoreKey(url, storeKey)
  );

  const { data: memberCardTurnoverData } = useSWR(
    () =>
      selectedStore?.storeKey && isKpiExists("Commercial", "MemberCardTurnover")
        ? [`/commercial/memberCardTurnover?range=${Range.LastMonth}&chain=SDV`, selectedStore.storeKey]
        : null,
    ([url, storeKey]) => fetcherWithStoreKey(url, storeKey)
  );

  const { data: pastTurnoverPromoData } = useSWR(
    () =>
      selectedStore?.storeKey && isKpiExists("Commercial", "TurnoverPromo")
        ? [`/commercial/turnoverPromo?range=${Range.LastMonthLastYear}`, selectedStore.storeKey]
        : null,
    ([url, storeKey]) => fetcherWithStoreKey(url, storeKey)
  );

  const { data: pastMemberCardTurnoverData } = useSWR(
    () =>
      selectedStore?.storeKey && isKpiExists("Commercial", "MemberCardTurnover")
        ? [`/commercial/memberCardTurnover?range=${Range.LastMonthLastYear}&chain=SDV`, selectedStore.storeKey]
        : null,
    ([url, storeKey]) => fetcherWithStoreKey(url, storeKey)
  );

  const { data: objectiveTurnoverData } = useSWR<TurnoverObjective>(
    () =>
      selectedStore?.storeKey && isKpiExists("Commercial", "Turnover")
        ? [`/commercial/objective?range=${Range.LastMonth}`,
        selectedStore.storeKey as string]
        : null,
    ([url, storeKey]) => fetcherWithStoreKey(url, storeKey as string)
  );

  const { data: driveTurnoverData } = useSWR(
    () =>
      selectedStore?.storeKey
        ? [`/drive/turnover?range=${Range.LastMonth}&ecomServiceType=${EcomServiceType.EcomTotal}`, selectedStore.storeKey]
        : null,
    ([url, storeKey]) => fetcherWithStoreKey(url, storeKey)
  );

  const { data: pastDriveTurnoverData } = useSWR(
    () =>
      selectedStore?.storeKey
        ? [`/drive/turnover?range=${Range.LastMonthLastYear}&ecomServiceType=${EcomServiceType.EcomTotal}`, selectedStore.storeKey]
        : null,
    ([url, storeEan]) => fetcherWithStoreKey(url, storeEan)
  );


  const { data: sectorData, error: sectorError } = useSWR(
    () =>
      selectedStore?.storeKey && isKpiExists("Commercial", "TurnoverBySector") && displayLegacyUi
        ? [`/commercial/sector?range=${Range.LastMonth}&storeType=${selectedStore.storeType.toUpperCase()}&sector=SDV`,
            selectedStore.storeKey, selectedStore.countryKey as string]
        : null,
    ([url, storeEan, countryKey]) => fetcherWithStoreKey(url, storeEan, countryKey as string)
  );

  const { data: driveObjectiveData } = useSWR(
    () =>
      selectedStore?.storeKey
        ? [`/drive/objective?range=${Range.LastMonth}&ecomServiceType=${EcomServiceType.Drive}`, selectedStore.storeKey]
        : null,
    ([url, storeEan]) => fetcherWithStoreKey(url, storeEan)
  );

  const { data: currentTurnoverTriptychData } = useSWR(
    () =>
      selectedStore?.storeKey && isKpiExists("Commercial", "TurnoverTriptych")
        ? [`/commercial/turnoverTriptych/allBrands?range=${Range.LastMonth}`, selectedStore.storeKey]
        : null,
    ([url, storeEan]) => fetcherWithStoreKey(url, storeEan)
  );

  const { data: pastTurnoverTriptychData } = useSWR(
    () =>
      selectedStore?.storeKey && isKpiExists("Commercial", "TurnoverTriptych")
        ? [`/commercial/turnoverTriptych/allBrands?range=${Range.LastMonthLastYear}`, selectedStore.storeKey]
        : null,
    ([url, storeEan]) => fetcherWithStoreKey(url, storeEan)
  );
  
  const contentPageProps = {
    hasTurnoverByHour: false,
    hasComparative: false,
    storeType: selectedStore?.storeType!,
    currentTurnoverPromoData: turnoverPromoData,
    currentMemberCardTurnoverData: memberCardTurnoverData,
    currentDriveData: driveTurnoverData,
    currentTurnoverData: turnoverData,
    driveObjective: driveObjectiveData,
    pastDriveData: pastDriveTurnoverData,
    pastTurnoverData: pastTurnoverData,
    pastTurnoverPromoData: pastTurnoverPromoData,
    pastMemberCardTurnoverData: pastMemberCardTurnoverData,
    sectorListData: sectorData,
    turnoverObjectiveData: objectiveTurnoverData!,
    comparativeList: undefined,
    currentTurnoverTriptychData: currentTurnoverTriptychData,
    pastTurnoverTriptychData: pastTurnoverTriptychData,
    isLoadingSector: !sectorData,
    isLoadingTurnover: false,
    isLoadingTurnoverByHour: false,
    isLoadingComparative: false,
    hasErrorSector: !!sectorError || sectorData === null || !sectorData?.[0]?.item?.length,
    hasErrorTurnover: false,
    hasErrorTurnoverByHour: false,
    hasErrorComparative: false,
    openInformationModal: () => null,
    range: Range.LastMonth,
  };

  return (
    <CommercialLayout>
      {displayLegacyUi ? (
        <CommercialContentPage {...contentPageProps} />
      ) : (
        <CommercialContentPageDesktop {...contentPageProps} />
      )}
    </CommercialLayout>
  );
}

export default LastMonth;
